<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="additionalModuleName" class="pt-3 pb-5" />
      <sidebar-form
        ref="form"
        v-model="params"
        :is-valid.sync="isFormValid"
        :inputs="inputs"
        is-editing-on
        hide-rules-icon
      />
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import SidebarForm from '@/components/SidebarForm';
export default {
  name: 'AntennaSignalDetermination',
  components: { CardTitleRow, SidebarForm },
  computed: {
    additionalModuleName: get('admin/additionalModules.ANTENNA_SIGNAL_BEARING_MODULE.verbose_name'),
    currentLayerId() {
      return +this.$route.params.lid;
    },
    currentLayerSelectedFeatures: get('map/selectedFeatures@:currentLayerId'),
    computedCurrentLayerSelectedFeatures() {
      return this.currentLayerSelectedFeatures || [];
    },
    inputs() {
      return [
        {
          name: 'alfa',
          cols: '6',
          nameCols: '5',
          dataType: { name: 'integer' },
          translationPath: 'sidebar.antennaSignalDetermination.alfa',
          hasSubtitle: true,
          subtitlePath: 'sidebar.antennaSignalDetermination.alfaSubtitle',
          rules: [
            v => !!v || v === 0 || this.$i18n.t('rules.required'),
            v => (v >= -360 && v <= 360) || this.$i18n.t('sidebar.antennaSignalDetermination.angleRule'),
          ],
        },
        {
          name: 'beta',
          cols: '6',
          nameCols: '5',
          dataType: { name: 'integer' },
          translationPath: 'sidebar.antennaSignalDetermination.beta',
          hasSubtitle: true,
          subtitlePath: 'sidebar.antennaSignalDetermination.betaSubtitle',
          rules: [
            v => !!v || v === 0 || this.$i18n.t('rules.required'),
            v => (v >= -360 && v <= 360) || this.$i18n.t('sidebar.antennaSignalDetermination.angleRule'),
          ],
        },
        {
          name: 'gamma',
          cols: '6',
          nameCols: '5',
          dataType: { name: 'integer' },
          translationPath: 'sidebar.antennaSignalDetermination.gamma',
          hasSubtitle: true,
          subtitlePath: 'sidebar.antennaSignalDetermination.gammaSubtitle',
          rules: [
            v => !!v || v === 0 || this.$i18n.t('rules.required'),
            v => (v >= -360 && v <= 360) || this.$i18n.t('sidebar.antennaSignalDetermination.angleRule'),
          ],
        },
        {
          name: 'radius',
          cols: '6',
          allowNegative: false,
          minValue: 1,
          nameCols: '5',
          dataType: { name: 'integer' },
          translationPath: 'sidebar.antennaSignalDetermination.circleRadius',
          rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
        },
      ];
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    params: {
      alfa: null,
      beta: null,
      gamma: null,
      radius: null,
    },
    isFormValid: false,
  }),
  methods: {
    getLayerFeature: call('layers/getLayerFeature'),
    async toggleAntennaSignalDetermination(value, params = this.params) {
      if (value) {
        this.isStickyButtonsLoading = true;
        try {
          const feature = await this.getLayerFeature({
            layer_id: this.currentLayerId,
            feature_id: this.computedCurrentLayerSelectedFeatures[0],
          });
          const geometry = { ...feature.geometry, crs: feature.crs };
          this.toggleMapAngles({ ...this.getMappedParams(params), geometry });
          this.stickyButtons = [
            {
              action: 'runAntennaSignalDetermination',
              translationPath: 'button.generateAgain',
            },
            {
              action: 'cancelAntennaSignalDetermination',
              translationPath: 'dialog.reset',
              cancel: true,
            },
          ];
          this.isStickyButtonsLoading = false;
        } catch {
          this.isStickyButtonsLoading = false;
        }
      } else {
        this.toggleMapAngles(false);
        this.params = {
          alfa: null,
          beta: null,
          gamma: null,
          radius: null,
        };
        this.$refs.form?.$refs.form?.resetValidation();
        this.stickyButtons = [
          {
            action: 'runAntennaSignalDetermination',
            translationPath: 'button.generate',
          },
        ];
      }
    },
    toggleMapAngles(params) {
      this.$root.$emit('toggleAntennaSignalDeterminationCircle', params);
    },
    getMappedParams(params) {
      return { ...params, beta: params.alfa + params.beta, gamma: params.alfa + params.gamma };
    },
  },
  mounted() {
    this.$root.$off('runAntennaSignalDetermination');
    this.$root.$on('runAntennaSignalDetermination', () => {
      this.toggleAntennaSignalDetermination(true);
    });
    this.$root.$off('cancelAntennaSignalDetermination');
    this.$root.$on('cancelAntennaSignalDetermination', () => {
      this.toggleAntennaSignalDetermination(false);
    });
    this.$watch(
      vm => {
        return { isFormValid: vm.isFormValid, selectedFeatures: vm.computedCurrentLayerSelectedFeatures };
      },
      nV => {
        const { isFormValid, selectedFeatures } = nV;
        this.isStickyButtonsValid = isFormValid && selectedFeatures?.length === 1;
      }
    );
    this.stickyButtons = [
      {
        action: 'runAntennaSignalDetermination',
        translationPath: 'button.generate',
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.toggleAntennaSignalDetermination(false);
  },
};
</script>
